/**
 * Shop Service
 * Manages shop operations (browse items, purchase, get purchases, equip items)
 */

import { supabase, supabaseRpc } from '@/lib/supabase';
import { isObtainable } from '@/lib/shopWindow';
import type { ShopItem, ShopItemType, ShopItemRarity } from '@/models/ShopItem';
import type { ShopPurchase } from '@/models/ShopPurchase';
import { useAnalytics } from '@/composables/useAnalytics';

/**
 * Le motif de refus d'un objet hors de sa fenêtre d'acquisition. C'est le texte
 * exact levé par la base (`guard_shop_spend_window`), repris tel quel pour que
 * l'écran reconnaisse le même refus qu'il vienne de l'app ou du serveur.
 */
export const ITEM_NOT_OBTAINABLE = 'item_not_obtainable';

const analytics = useAnalytics();

export const shopService = {
  /**
   * Get list of available shop items (filtered by type and/or rarity).
   * (Plus de filtre par `category` : la colonne n'est lue nulle part, le lien
   * animal ↔ poses passe par `metadata.animal_id`.)
   * Only rows with is_active=true, is_exclusive=false, is_purchasable=true are returned.
   * For new animals: type='animal', metadata.animal_id set. For poses: type='pose', metadata.pose_id + metadata.animal_id.
   * @param type Optional filter by type ('animal', 'pose', 'background', 'accessory')
   * @param rarity Optional filter by rarity ('basic', 'standard', 'premium')
   * @returns Array of shop items
   */
  async getShopItems(
    type?: ShopItemType,
    rarity?: ShopItemRarity
  ): Promise<ShopItem[]> {
    let query = (supabase as any)
      .from('shop_items')
      .select('*')
      .eq('is_active', true)
      .eq('is_exclusive', false)
      .eq('is_purchasable', true)
      .order('price', { ascending: true })
      .order('name', { ascending: true });

    if (type) {
      query = query.eq('type', type);
    }

    if (rarity) {
      query = query.eq('rarity', rarity);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error getting shop items:', error);
      throw new Error(`Failed to get shop items: ${error.message}`);
    }

    return (data || []) as unknown as ShopItem[];
  },

  /**
   * Purchase a shop item for a child
   * Uses database transaction to ensure atomicity (deduct coins + create purchase record)
   * @param childId UUID of the child making purchase
   * @param shopItemId UUID of the shop item to purchase
   * @returns Purchase record
   */
  async purchaseItem(childId: string, shopItemId: string): Promise<ShopPurchase> {
    // First, get the shop item to check price and validity
    const { data: shopItem, error: itemError } = await (supabase as any)
      .from('shop_items')
      .select('*')
      .eq('id', shopItemId)
      .eq('is_active', true)
      .eq('is_purchasable', true)
      .single();

    if (itemError || !shopItem) {
      throw new Error('Item not found or not available for purchase');
    }

    if (shopItem.is_exclusive) {
      throw new Error('Item is exclusive and cannot be purchased');
    }

    // Hors de sa fenêtre d'acquisition, on s'arrête AVANT le débit. Le serveur
    // refuserait de toute façon et annulerait le débit avec lui, mais c'est un
    // aller-retour pour rien et un message d'erreur moins clair.
    if (!isObtainable(shopItem)) {
      throw new Error(ITEM_NOT_OBTAINABLE);
    }

    // Check if already purchased (using unique constraint check)
    const { data: existingPurchase } = await (supabase as any)
      .from('child_shop_purchases')
      .select('id')
      .eq('child_id', childId)
      .eq('shop_item_id', shopItemId)
      .single();

    if (existingPurchase) {
      throw new Error('Already purchased');
    }

    // Get current coin balance
    const { data: child, error: childError } = await (supabase as any)
      .from('children')
      .select('coins, points')
      .eq('id', childId)
      .single();

    if (childError || !child) {
      throw new Error('Child not found');
    }

    if (Number((child as any).coins ?? (child as any).points ?? 0) < (shopItem as any).price) {
      throw new Error('Insufficient coins');
    }

    // Use a transaction-like approach: deduct coins first, then create purchase
    // Since Supabase doesn't support explicit transactions in the client,
    // we'll use the database function for coin deduction which is atomic
    // and then create the purchase record
    try {
      // Deduct coins using the database function (atomic)
      const { error: deductError } = await supabaseRpc.rpc('deduct_coins_from_child', {
        p_child_id: childId,
        p_amount: (shopItem as any).price,
        p_source: 'shop_purchase',
        p_reference_id: shopItemId
      });

      if (deductError) {
        if (deductError.message?.includes('Insufficient coins')) {
          throw new Error('Insufficient coins');
        }
        // La fenêtre a pu se fermer entre l'affichage et le toucher.
        if (deductError.message?.includes(ITEM_NOT_OBTAINABLE)) {
          throw new Error(ITEM_NOT_OBTAINABLE);
        }
        throw new Error(`Purchase failed: ${deductError.message}`);
      }

      // Le diagnostic RLS qui vivait ici tournait en PRODUCTION à chaque achat :
      // un RPC `debug_shop_purchase_rls` dont le seul effet était d'écrire des
      // logs, un `auth.getUser()` (aller-retour GoTrue) pour journaliser un id,
      // et plusieurs console.log dont un JSON.stringify indenté. Chaque log
      // devient un breadcrumb Sentry, c'est-à-dire un aller-retour de pont
      // natif. Tout ça sur le geste le plus attendu de la boutique. L'erreur
      // d'insertion ci-dessous dit déjà ce qu'il faut savoir si la RLS refuse ;
      // la fonction de debug reste en base pour un diagnostic à la demande.

      // Create purchase record
      const { data: purchase, error: purchaseError } = await (supabase as any)
        .from('child_shop_purchases')
        .insert({
          child_id: childId,
          shop_item_id: shopItemId,
          coins_spent: shopItem.price,
          is_equipped: false
        })
        .select()
        .single();

      if (purchaseError) {
        // If purchase record creation fails, we should rollback the coin deduction
        // However, since we can't do transactions, we'll log the error
        // In production, this should be handled by a database trigger or edge function
        console.error('Purchase record creation failed after coin deduction:', purchaseError);
        throw new Error(`Purchase failed: ${purchaseError.message}`);
      }

      // Integrate with avatar system: if item is animal/background,
      // mark it as available in child_avatars (via equipItem if needed)
      // Note: The item is now available for the child to equip via equipItem method
      // This integration ensures purchased items are available for use in the avatar system

      // If this is an avatar (animal), grant the default pose for free
      console.log('[shopService] Checking for pose grant - type:', shopItem.type, 'metadata:', JSON.stringify(shopItem.metadata));
      if (shopItem.type === 'animal' && shopItem.metadata?.animal_id) {
        console.log('[shopService] Granting default pose for animal:', shopItem.metadata.animal_id);
        await this.grantDefaultPoseForAnimal(childId, shopItem.metadata.animal_id);
      } else {
        console.log('[shopService] Skipping pose grant - not an animal or no animal_id');
      }

      // Track shop purchase in analytics
      try {
        await analytics.logShopPurchase(
          childId,
          shopItemId,
          shopItem.name,
          shopItem.type,
          shopItem.price,
          shopItem.rarity
        );
      } catch (analyticsError) {
        // Don't fail purchase if analytics fails
        console.warn('[shopService] Analytics tracking failed (non-critical):', analyticsError);
      }

      return purchase as ShopPurchase;
    } catch (error) {
      // Re-throw with proper error message
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Purchase failed');
    }
  },

  /**
   * Grant the default pose for an animal when purchasing the animal
   * This ensures users can actually use the avatar they purchased
   * @param childId UUID of the child
   * @param animalId ID of the animal (from metadata)
   */
  async grantDefaultPoseForAnimal(childId: string, animalId: string): Promise<void> {
    console.log('[shopService] grantDefaultPoseForAnimal called with:', { childId, animalId });
    
    try {
      // Find the default/cheapest pose for this animal in shop_items
      const { data: poses, error: posesError } = await (supabase as any)
      .from('shop_items')
        .select('id, name, price, metadata')
        .eq('type', 'pose')
        .eq('is_active', true)
        .order('price', { ascending: true });

      console.log('[shopService] Found poses count:', poses?.length, 'error:', posesError);

      if (posesError || !poses) {
        console.warn('[shopService] Could not find poses for animal:', animalId, 'error:', posesError);
        return;
      }

      // Filter poses for this animal (metadata.animal_id matches)
      const animalPoses = poses.filter((pose: any) => {
        const match = pose.metadata?.animal_id === animalId;
        console.log('[shopService] Pose check:', pose.name, 'metadata.animal_id:', pose.metadata?.animal_id, 'vs', animalId, '=', match);
        return match;
      });

      console.log('[shopService] Filtered poses for', animalId, ':', animalPoses.length);

      if (animalPoses.length === 0) {
        console.warn('[shopService] No poses found for animal:', animalId);
        return;
      }

      // Get the cheapest pose (likely the default one)
      const defaultPose = animalPoses[0];
      console.log('[shopService] Default pose selected:', defaultPose.name, 'id:', defaultPose.id);

      // Check if already purchased
      const { data: existingPurchase } = await (supabase as any)
        .from('child_shop_purchases')
        .select('id')
        .eq('child_id', childId)
        .eq('shop_item_id', defaultPose.id)
        .single();

      if (existingPurchase) {
        console.log('[shopService] Default pose already owned:', defaultPose.name);
        return;
      }

      // Grant the pose for free (0 coins spent)
      const { error: grantError } = await (supabase as any)
        .from('child_shop_purchases')
        .insert({
          child_id: childId,
          shop_item_id: defaultPose.id,
          coins_spent: 0, // Free with animal purchase
          is_equipped: false
        });

      if (grantError) {
        console.error('[shopService] Failed to grant default pose:', grantError);
        // Don't throw - animal purchase was successful, pose grant is a bonus
      } else {
        console.log('[shopService] Granted default pose:', defaultPose.name, 'for animal:', animalId);
      }
    } catch (err) {
      console.error('[shopService] Error granting default pose:', err);
      // Don't throw - animal purchase was successful
    }
  },

  /**
   * Get list of items purchased by a child
   * @param childId UUID of the child
   * @returns Array of purchase records with item details
   */
  async getPurchasedItems(childId: string): Promise<ShopPurchase[]> {
    const query = (supabase as any)
      .from('child_shop_purchases')
      .select(`
        *,
        shop_item:shop_items(*)
      `)
      .eq('child_id', childId)
      .order('purchased_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error getting purchased items:', error);
      throw new Error(`Failed to get purchased items: ${error.message}`);
    }

    // Map the data to include shop_item in the purchase object
    return (data || []).map((purchase: any) => ({
      ...purchase,
      shop_item: purchase.shop_item || undefined
    })) as unknown as ShopPurchase[];
  },

  /**
   * Equip or unequip a purchased item
   * Updates both child_shop_purchases and child_avatars tables
   * @param childId UUID of the child
   * @param purchaseId UUID of the purchase record
   * @param isEquipped true to equip, false to unequip
   */
  async equipItem(childId: string, purchaseId: string, isEquipped: boolean): Promise<void> {
    // First verify the purchase belongs to the child and get item details
    const { data: purchase, error: checkError } = await (supabase as any)
      .from('child_shop_purchases')
      .select(`
        child_id,
        shop_item:shop_items(type, metadata)
      `)
      .eq('id', purchaseId)
      .single();

    if (checkError || !purchase) {
      throw new Error('Purchase not found');
    }

    if (purchase.child_id !== childId) {
      throw new Error('Purchase does not belong to child');
    }

    // Update the equipped status in child_shop_purchases
    const { error: updateError } = await (supabase as any)
      .from('child_shop_purchases')
      .update({ is_equipped: isEquipped })
      .eq('id', purchaseId);

    if (updateError) {
      console.error('Error updating equipped status:', updateError);
      throw new Error(`Update failed: ${updateError.message}`);
    }

    // Integrate with avatar system: update child_avatars based on item type
    if (isEquipped && purchase.shop_item) {
      const shopItem = purchase.shop_item as any;
      const metadata = shopItem.metadata || {};
      
      // Get or create child_avatars record
      const { data: avatarConfig } = await (supabase as any)
        .from('child_avatars')
        .select('*')
        .eq('child_id', childId)
        .single();

      const updateData: any = {};

      if (shopItem.type === 'animal' && metadata.animal_id) {
        // Update animal_id if it's an avatar
        updateData.animal_id = metadata.animal_id;
      } else if (shopItem.type === 'background' && metadata.background_id) {
        // Update equipped_background if it's a background
        updateData.equipped_background = metadata.background_id;
      }

      // Update child_avatars if there are changes
      if (Object.keys(updateData).length > 0) {
        if (avatarConfig) {
          // Update existing record
          await supabase
            .from('child_avatars')
            .update(updateData)
            .eq('child_id', childId);
        } else {
          // Create new record
          await supabase
            .from('child_avatars')
            .insert({
              child_id: childId,
              ...updateData
            });
        }
      }
    }
    // Unequipping leaves child_avatars alone: an animal or a background stays
    // available, just not currently active.
  },

  /**
   * Grant free starter items to a new child
   * Randomly selects and gifts 1 basic avatar, 1 compatible pose, and 1 background
   * Creates purchase records with coins_spent=0 and is_gifted=true
   * @param childId UUID of the child receiving the gifts
   * @returns Object containing the granted item IDs
   */
  async grantFreeStarterItems(childId: string): Promise<{
    animalId: string
    poseId: string
    backgroundId: string
  }> {
    try {
      // Step 0: Check if child already received free starter items
      const { data: existingGifts, error: giftCheckError } = await (supabase as any)
        .from('child_shop_purchases')
        .select('id')
        .eq('child_id', childId)
        .eq('is_gifted', true)
        .limit(1);

      if (giftCheckError) {
        console.warn('[shopService] Error checking existing gifts:', giftCheckError);
        // Don't throw - proceed with caution
      }

      if (existingGifts && existingGifts.length > 0) {
        console.log(`[shopService] Child ${childId} already has gifted items, skipping grant`);
        // Return existing avatar config
        const { data: avatarConfig } = await supabase
          .from('child_avatars')
          .select('*')
          .eq('child_id', childId)
          .single();

        if (avatarConfig) {
          return {
            animalId: avatarConfig.animal_id || 'dog',
            poseId: avatarConfig.equipped_pose || 'dog-pose-happy',
            backgroundId: avatarConfig.equipped_background || 'bg-all-forest',
          };
        }
      }

      // Step 1: Get all basic items
      const { data: allBasicItems, error: itemsError } = await (supabase as any)
      .from('shop_items')
        .select('*')
        .eq('is_active', true)
        .eq('rarity', 'basic')
        .in('type', ['animal', 'pose', 'background']);

      if (itemsError) {
        console.error('Error fetching basic items:', itemsError);
        throw new Error('Failed to fetch starter items');
      }

      if (!allBasicItems || allBasicItems.length === 0) {
        throw new Error('No basic starter items available');
      }

      // Step 2: Filter to level 0 items and separate by type
      const basicItems = allBasicItems.filter(item => {
        const unlockLevel = item.metadata?.unlock_level ?? 0;
        return unlockLevel === 0;
      });

      const avatarItems = basicItems.filter(item => item.type === 'animal');
      const poseItems = basicItems.filter(item => item.type === 'pose');
      const backgroundItems = basicItems.filter(item => item.type === 'background');

      if (avatarItems.length === 0 || backgroundItems.length === 0) {
        throw new Error('Missing required starter items (avatar or background)');
      }

      // Step 3: Randomly select items
      const selectedAvatar = avatarItems[Math.floor(Math.random() * avatarItems.length)];
      const selectedAnimalId = selectedAvatar.metadata?.animal_id || 'dog';

      // Filter poses compatible with selected animal
      const compatiblePoses = poseItems.filter(
        item => item.metadata?.animal_id === selectedAnimalId
      );

      const selectedPose = compatiblePoses.length > 0
        ? compatiblePoses[Math.floor(Math.random() * compatiblePoses.length)]
        : poseItems[0]; // Fallback to any pose if no compatible poses

      const selectedBackground = backgroundItems[Math.floor(Math.random() * backgroundItems.length)];

      // Step 4: Create purchase records for all three items
      const purchaseRecords = [
        {
          child_id: childId,
          shop_item_id: selectedAvatar.id,
          coins_spent: 0,
          is_equipped: true,
          is_gifted: true,
        },
        {
          child_id: childId,
          shop_item_id: selectedPose.id,
          coins_spent: 0,
          is_equipped: true,
          is_gifted: true,
        },
        {
          child_id: childId,
          shop_item_id: selectedBackground.id,
          coins_spent: 0,
          is_equipped: true,
          is_gifted: true,
        },
      ];

      const { error: purchaseError } = await (supabase as any)
        .from('child_shop_purchases')
        .insert(purchaseRecords);

      if (purchaseError) {
        console.error('Error creating gift purchase records:', purchaseError);
        throw new Error(`Failed to grant starter items: ${purchaseError.message}`);
      }

      // Step 5: Update child_avatars with the selected items
      const poseId = selectedPose.metadata?.pose_id || 'dog-pose-happy';
      const backgroundId = selectedBackground.metadata?.background_id || 'bg-all-forest';

      const { error: avatarError } = await (supabase as any)
        .from('child_avatars')
        .upsert({
          child_id: childId,
          animal_id: selectedAnimalId,
          equipped_pose: poseId,
          equipped_accessories: [],
          equipped_background: backgroundId,
        });

      if (avatarError) {
        console.error('Error updating child avatar config:', avatarError);
        // Don't throw here - purchases were created successfully
        console.warn('Avatar config update failed but gifts were granted');
      }

      console.log(`[shopService] Granted free starter items to child ${childId}:`, {
        animal: selectedAnimalId,
        pose: poseId,
        background: backgroundId,
      });

      return {
        animalId: selectedAnimalId,
        poseId,
        backgroundId,
      };
    } catch (error) {
      console.error('[shopService] Error granting free starter items:', error);
      throw error;
    }
  }
};
